import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import {
  Home as HomeIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Toolbar>
          <IconButton edge="start" color="primary" sx={{ mr: 2 }} onClick={() => navigate('/')}>
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 600 }}>
            QuickBuildr.ai
          </Typography>
          {isAuthenticated ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Content */}
      <Container maxWidth="md">
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h2" fontWeight={700} color="primary.main" gutterBottom>
              Terms of Service
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Last updated: January 27, 2025
            </Typography>

            <Paper elevation={0} sx={{ p: 4, bgcolor: 'white' }}>
              <Typography variant="h5" fontWeight={600} gutterBottom sx={{ mt: 4 }}>
                1. Acceptance of Terms
              </Typography>
              <Typography variant="body1" paragraph>
                By accessing and using QuickBuildr.ai ("the Service"), you agree to be bound by these 
                Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                2. Description of Service
              </Typography>
              <Typography variant="body1" paragraph>
                QuickBuildr.ai provides property development feasibility analysis for real estate. 
                AI-powered analysis is currently available in Somerville, Cambridge, and Lexington MA. 
                Professional architectural studies are available nationwide. Our services include:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Basic Analysis: $20 per property analysis with approximate floor plans</li>
                <li>Pro Subscription: $80 per month for unlimited property searches</li>
                <li>Enterprise Study: $3,000 for stamped architectural feasibility study delivered within 2-3 business days</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                3. User Accounts
              </Typography>
              <Typography variant="body1" paragraph>
                To access certain features of the Service, you must create an account. You are responsible for:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Providing accurate and complete registration information</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                4. Payment Terms
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Basic Analysis:</strong> Payment of $20 is required per analysis. Each analysis is 
                non-refundable once generated.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Pro Subscription:</strong> Billed monthly at $80. You may cancel at any time, 
                with access continuing until the end of the billing period.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Enterprise Study:</strong> Payment of $3,000 is required upfront. Delivered within 
                48 business hours (2-3 business days). Includes one round of revisions.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                5. Acceptable Use
              </Typography>
              <Typography variant="body1" paragraph>
                You agree not to:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Attempt to access unauthorized areas of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Attempt to reverse engineer the Service</li>
                <li>Resell or redistribute the Service without permission</li>
                <li>Use automated systems to access the Service without permission</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                6. Intellectual Property
              </Typography>
              <Typography variant="body1" paragraph>
                All content, features, and functionality of the Service are owned by QuickBuildr.ai 
                and are protected by international copyright, trademark, and other intellectual property laws.
              </Typography>
              <Typography variant="body1" paragraph>
                Reports and analyses generated for you through the Service are provided for your use, 
                but the underlying technology and methodologies remain our property.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                7. Data Accuracy and Disclaimer
              </Typography>
              <Typography variant="body1" paragraph>
                While we strive to provide accurate information based on official data sources, 
                QuickBuildr.ai makes no warranties about the completeness, reliability, or accuracy 
                of the information provided. The Service is provided "as is" and should not be the 
                sole basis for investment decisions.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Important:</strong> Always verify information with official sources and consult 
                with qualified professionals before making real estate decisions.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                8. Limitation of Liability
              </Typography>
              <Typography variant="body1" paragraph>
                To the maximum extent permitted by law, QuickBuildr.ai shall not be liable for any 
                indirect, incidental, special, consequential, or punitive damages resulting from your 
                use or inability to use the Service.
              </Typography>
              <Typography variant="body1" paragraph>
                Our total liability for any claim arising from these Terms or the Service shall not 
                exceed the amount paid by you to QuickBuildr.ai in the twelve months preceding the claim.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                9. Indemnification
              </Typography>
              <Typography variant="body1" paragraph>
                You agree to indemnify and hold harmless QuickBuildr.ai, its officers, directors, 
                employees, and agents from any claims, damages, losses, or expenses arising from your 
                use of the Service or violation of these Terms.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                10. Termination
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to suspend or terminate your account and access to the Service 
                at our discretion, without notice, for conduct that we believe violates these Terms 
                or is harmful to other users or the Service.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                11. Governing Law
              </Typography>
              <Typography variant="body1" paragraph>
                These Terms are governed by the laws of the Commonwealth of Massachusetts, without 
                regard to its conflict of law provisions. Any disputes shall be resolved in the 
                courts of Massachusetts.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                12. Changes to Terms
              </Typography>
              <Typography variant="body1" paragraph>
                We reserve the right to modify these Terms at any time. We will notify users of any 
                material changes via email or through the Service. Continued use of the Service after 
                changes constitutes acceptance of the modified Terms.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                13. Refund Policy
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Basic Analysis:</strong> Non-refundable once the analysis is generated.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Pro Subscription:</strong> No refunds for partial months. Cancel anytime for 
                the next billing cycle.
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Enterprise Study:</strong> 50% refund if cancelled within 24 hours of order 
                and before work has begun. No refunds once work has commenced.
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h5" fontWeight={600} gutterBottom>
                14. Contact Information
              </Typography>
              <Typography variant="body1" paragraph>
                For questions about these Terms, please contact us:
              </Typography>
              <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
                <li>By phone: 781-528-3339</li>
                <li>By email: sales@quickbuildr.ai</li>
                <li>By mail: QuickBuildr.ai, Boston, MA</li>
                <li>Through our contact form at quickbuildr.ai/contact</li>
              </Typography>

              <Divider sx={{ my: 4 }} />

              <Typography variant="body1" sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                By using QuickBuildr.ai, you acknowledge that you have read, understood, and agree 
                to be bound by these Terms of Service.
              </Typography>
            </Paper>
          </motion.div>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.dark', color: 'white', py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" textAlign="center" sx={{ opacity: 0.8 }}>
            © 2025 QuickBuildr.ai. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default TermsPage;